<script>
import { h, toHandlers } from 'vue';
import Popover from './Popover';
import PopoverRef from './PopoverRef';
import CalendarNav from './CalendarNav';
import CalendarDay from './CalendarDay';
import Grid from './Grid';
import {
  propOrDefaultMixin,
  childMixin,
  safeScopedSlotMixin,
} from '../utils/mixins';
import { createGuid } from '../utils/helpers';

export default {
  name: 'CalendarPane',
  inheritAttrs: false,
  mixins: [propOrDefaultMixin, childMixin, safeScopedSlotMixin],
  setup () {
    const dayRefs = { list: [] }

    return {
      dayRefs
    }
  },
  render() {
    // Header
    const header =
      this.safeScopedSlot('header', this.page) ||
      h(
        'div',
        {
          class: ['vc-header', this.theme.header],
        },
        [
          // Header title
          h(
            'div',
            {
              class: `vc-title-layout align-${this.titlePosition}`,
            },
            [
              h('div', { class: 'vc-title-wrapper' }, [
                // Navigation popover ref with title
                h(
                  PopoverRef,
                  {
                    id: this.navPopoverId,
                    visibility: this.navVisibility_,
                    placement: this.navPlacement,
                    modifiers: { flip: { behavior: ['bottom'] } },
                    isInteractive: true,
                  },
                  {
                    default: () => [
                      h('div', { class: ['vc-title', this.theme.title] }, [
                        this.safeScopedSlot(
                          'header-title',
                          this.page,
                          this.page.title,
                        ),
                      ]),
                    ],
                  },
                ),
                // Navigation popover
                h(
                  Popover,
                  {
                    id: this.navPopoverId,
                    contentClass: this.theme.navPopoverContainer,
                  },
                  {
                    default: () => [
                      h(
                        CalendarNav,
                        {
                          value: this.page,
                          validator: this.canMove,
                          ...toHandlers({
                            input: $event => this.move($event),
                          }),
                        },
                        this.$slots,
                      ),
                    ],
                  },
                ),
              ]),
            ],
          ),
        ],
      );

    this.dayRefs.list = []

    // Weeks
    const weeks = h(
      Grid,
      {
        class: 'vc-weeks',
        rows: 7,
        columns: 7,
        columnWidth: '1fr',
        disableFocus: true,
      },
      {
        default: () => [
          ...this.weekdayLabels.map((wl, i) =>
            h(
              'div',
              {
                key: i + 1,
                class: ['vc-weekday', this.theme.weekdays],
              },
              [wl],
            ),
          ),
          ...this.page.days.map(day =>
            h(
              CalendarDay,
              {
                ...this.$attrs,
                day,
                key: day.id,
                ref: (component) => {
                  if(component) this.dayRefs.list.push(component)
                },
              },
              this.$slots,
            ),
          ),
        ],
      },
    );

    return h(
      'div',
      {
        class: 'vc-pane',
        ref: 'pane',
      },
      [header, weeks],
    );
  },
  props: {
    page: Object,
    titlePosition: String,
    navVisibility: String,
    canMove: {
      type: Function,
      default: () => true,
    },
  },
  data() {
    return {
      navPopoverId: createGuid(),
    };
  },
  computed: {
    navVisibility_() {
      return this.propOrDefault('navVisibility', 'navVisibility');
    },
    navPlacement() {
      switch (this.titlePosition) {
        case 'left':
          return 'bottom-start';
        case 'right':
          return 'bottom-end';
        default:
          return 'bottom';
      }
    },
    weekdayLabels() {
      return this.locale
        .getWeekdayDates()
        .map(d => this.format(d, this.masks.weekdays));
    },
  },
  methods: {
    move(page) {
      this.$emit('update:page', page);
    },
    refresh() {
      this.dayRefs.list.forEach(dayRef => dayRef.refresh());
    },
  },
};
</script>

<style lang="postcss" scoped>
.vc-pane {
  flex-grow: 1;
  flex-shrink: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
}

.vc-horizontal-divider {
  align-self: center;
}

.vc-header {
  flex-shrink: 0;
  display: flex;
  align-items: stretch;
  user-select: none;
  padding: var(--header-padding);
}

.vc-header.align-left {
  order: -1;
  justify-content: flex-start;
}

.vc-header.align-right {
  order: 1;
  justify-content: flex-end;
}

.vc-title-layout {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
}

.vc-title-layout.align-left {
  justify-content: flex-start;
}

.vc-title-layout.align-right {
  justify-content: flex-end;
}

.vc-title-wrapper {
  position: relative;
}

.vc-title {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  padding: var(--title-padding);
}

.vc-weekday {
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  padding: var(--weekday-padding);
  cursor: default;
  user-select: none;
}

.vc-weeks {
  flex-shrink: 1;
  flex-grow: 1;
  padding: var(--weeks-padding);
}
</style>
